/**
 * Declarative render scene — what every adapter (DOM, CLI, TUI)
 * agrees should appear on screen for a column range.
 *
 * The scene is a fully resolved value: stable-keyed nodes carrying
 * binding + plan + layout + intent payloads. No platform output
 * types appear here; reconcilers consume diffs against the scene to
 * produce host-specific output (DOM elements, terminal rows, etc.).
 *
 * `RenderPlan.makeForRows` decides per-cell mode/state. The scene
 * layers row-level overlays (fixed-children, legends) on top of the
 * per-cell plans so the rendering data model is a single tree, not
 * a row-major plan list plus ad-hoc per-row passes.
 */

import type { Option } from "../core/option.ts";
import type { NaiveDate } from "@tento-chrono";

import type { CellEntry } from "../core/dtype.ts";
import type { CellLayout, ColumnKey, Timescale } from "./layout-types.ts";
import type { FixedChildIntent } from "../providers/plugin-providers.ts";
import type { RenderBindings } from "./render-plugin-core.ts";
import type { RenderPlan } from "./render-plan.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { SceneNodeStamp } from "../stores/scene-node-stamp.ts";

/** Stable identity for any scene node. */
export type SceneKey =
  | { readonly kind: "cell"; readonly row: RowLocalId; readonly column: ColumnKey }
  | { readonly kind: "row-overlay"; readonly row: RowLocalId; readonly pluginId: string }
  | { readonly kind: "legend"; readonly row: RowLocalId };

/** Values that go with `SceneKey`: `toString`. */
export namespace SceneKey {
  /**
   * Serialize a `SceneKey` to a string suitable as a `Map` key.
   * Reconcilers and `diffScene` use this; never inspect the format.
   */
  export function toString(key: SceneKey): string {
    switch (key.kind) {
      case "cell":
        return `cell|${key.row.toString()}|${key.column.offset}`;
      case "row-overlay":
        return `overlay|${key.row.toString()}|${key.pluginId}`;
      case "legend":
        return `legend|${key.row.toString()}`;
    }
  }
}

/**
 * Scene-level frame revision — the read-mark + activation key the live
 * dual-run stamps onto a built scene. `Option` on the scene:
 * legacy / CLI / TUI callers omit it and see zero behavior change (the
 * classifier / diff never require it). Mirrors `GeometrySnapshot.Revision`.
 *
 * - `tau`: the sealed read mark the reader pinned (the TX0 recorder's
 *   `tau()` — engine-spec §3 read-marks). Never sampled ambiently.
 * - `epoch`: a scene artifact sequence number the live runner increments per
 *   capture — explicitly NOT the binder's `boundGeneration`.
 */
export interface SceneRevision {
  readonly tau: number;
  readonly epoch: number;
}

// The per-node output stamp is declared beside the stores whose versions it
// carries (`../stores/scene-node-stamp.ts`), so the sheet answers one without
// reaching the scene; it is published from here under its name.
export type { SceneNodeStamp } from "../stores/scene-node-stamp.ts";

/**
 * In-artifact count-of-work for one scene build (engine-spec §0 law 5 /
 * REQ-OBS-3, mirroring `GeometrySnapshot.BuildCounters`). Carried inside the
 * built scene so fixtures assert amount-of-work from the artifact itself.
 */
export interface SceneBuildCounters {
  /** Cell nodes freshly constructed this build (not reused from `prior`). */
  readonly nodesBuilt: number;
  /** Cell nodes reused by reference from the `prior` scene (memo hit). */
  readonly nodesReused: number;
  /**
   * Stamp-provider reads performed — one per ROW the builder visited (the row's
   * cells share one `data:` output version, so the probe reads once per row, not
   * per cell). Zero when no `stampProvider` was supplied.
   */
  readonly ledgerReads: number;
  /**
   * Rows whose nodes this build made (every row of a full build); the rows of
   * an incremental build whose input changed.
   */
  readonly rowsBuilt: number;
  /**
   * Rows an incremental build carried from `prior` by reference without
   * visiting their cells (their `SceneRowInput` was the same object).
   */
  readonly rowsCarried: number;
  /**
   * What a build that reuses by inputs (`reuse: "inputs"`) was made from: its
   * columns and, per row, the `SceneRowInput` object it was given (identity
   * only, never read). The next such build carries a row whose input is the
   * same object without visiting its cells, and `diffScene` compares only the
   * rows whose input changed. `null` for every other build (stamps reuse).
   */
  readonly inputs: Option<{ readonly columns: readonly ColumnKey[]; readonly rows: ReadonlyMap<RowLocalId, object> }>;
}


/**
 * Optional per-cell glyph hints. Reconcilers render at most one
 * trailing glyph per cell with precedence `syncing > stale > formula
 * > external`. Set by scene-input contributors that know which state
 * applies (e.g. the formula plugin sets `formula = true` for
 * computed cells; the sync layer sets `syncing` / `stale`).
 */
export interface CellGlyphs {
  readonly formula?: boolean;
  readonly syncing?: boolean;
  readonly stale?: boolean;
  readonly external?: boolean;
}

/**
 * Resolved cell payload a reconciler renders. Optional on `CellNode`
 * — callers populate it for cells whose content is a single value
 * (text, number, checkmark, formula). Overlay-bearing rows (calendar,
 * task) carry their content via `RowOverlayNode.intents` instead;
 * their `CellContent` is `{ kind: "none" }` or absent.
 *
 * Discriminated to leave room for richer per-cell payloads later
 * (`{ kind: "items"; itemIds: ... }` for stacked subcells, etc.)
 * without breaking the closed `kind: "value"` arm.
 */
export type CellContent =
  | { readonly kind: "none" }
  | {
      readonly kind: "value";
      readonly entry: Option<CellEntry>;
      readonly glyphs?: CellGlyphs;
    };

/** A single cell node — what a platform should draw at (row, column). */
export interface CellNode<K extends ColumnKey = ColumnKey> {
  readonly key: SceneKey & { readonly kind: "cell" };
  readonly rowIndex: number;
  readonly columnIndex: number;
  readonly columnDate: NaiveDate;
  readonly bindings: RenderBindings;
  readonly plan: RenderPlan<K>;
  /**
   * Per-cell geometry, kept ON the node so headless CLI / TUI consumers
   * render straight from the scene. NOTE (coverage boundary): the scene
   * re-carries geometry here in violation of the content/geometry split
   * (`geometry-snapshot.ts` owns offsets/sizes). Retiring `layout` from the
   * scene is a NAMED follow-up, gated on the renderer consuming `GeometrySnapshot`; until
   * then the diff MUST keep comparing `layout` (its resize fast path depends on
   * it). Do NOT stop comparing or drop it here.
   */
  readonly layout: Option<CellLayout>;
  /**
   * Resolved render payload. `null` ⇒ caller didn't populate (legal
   * for adapters that don't need value-level rendering, e.g. the
   * CLI's plan-inspection output). Reconcilers gate display on
   * `kind` being `"value"` with a non-null entry.
   */
  readonly content: Option<CellContent>;
  /**
   * Output stamp (LR6 / L5) — `Option`. When present, the diff takes the
   * version fast-path and the build reuses this node by reference across
   * frames. `null` ⇒ today's deep-compare behavior.
   */
  readonly stamp: Option<SceneNodeStamp>;
}

/**
 * Per-row overlay (spanned event bar, all-day strip, drag preview).
 * Holds the pre-resolved `FixedChildIntent[]` from the row's plugin
 * `dataProvider.fixedChildren`. Empty intent list ⇒ no overlay node
 * is emitted.
 */
export interface RowOverlayNode {
  readonly key: SceneKey & { readonly kind: "row-overlay" };
  readonly rowIndex: number;
  readonly bindings: RenderBindings;
  readonly intents: readonly FixedChildIntent[];
}

/** Sheet section a row belongs to. */
export type RowSection = "header" | "body" | "footer";

/** Per-row legend / chrome (label + plugin binding). */
export interface LegendNode {
  readonly key: SceneKey & { readonly kind: "legend" };
  readonly rowIndex: number;
  readonly bindings: RenderBindings;
  readonly label: string;
  /**
   * Row's own timescale, sourced from the contributing
   * `SceneRowInput`. Required for owner-period detection in spanned
   * rows and for stacked-subcell label generation. Defaults to the
   * scene's sheet timescale when the contributor doesn't know.
   */
  readonly timescaleSetting: Timescale | "custom";
  /**
   * Whether the row is system-synthesized (frozen header / footer
   * row, formula-computed system row). Renderers may prefix the
   * label or apply distinct styling.
   */
  readonly isSystemRow: boolean;
  /**
   * Sheet section the row belongs to. Renderers draw heavier
   * separators between section transitions (header → body, body →
   * footer).
   */
  readonly section: RowSection;
}

/** Full declarative output for one column range. */
export interface RenderScene<K extends ColumnKey = ColumnKey> {
  readonly columnRange: NaiveDate.Range;
  readonly timescale: Timescale;
  readonly cells: ReadonlyMap<string, CellNode<K>>;
  readonly rowOverlays: ReadonlyMap<string, RowOverlayNode>;
  readonly legends: ReadonlyMap<string, LegendNode>;
  /**
   * Frame revision (`{ tau, epoch }`), `null` when unstamped. Stamped by the
   * live dual-run; `null` for legacy / CLI / TUI callers (zero behavior change).
   * Excluded from `diffScene` (two scenes of identical content at different
   * `tau`/`epoch` diff empty).
   */
  readonly revision: Option<SceneRevision>;
  /**
   * In-artifact count-of-work for this build, `null` for callers that don't
   * build through `Scene.build` (e.g. `scene-serde` deserialize).
   */
  readonly work: Option<SceneBuildCounters>;
}

/** Discriminated union for any scene node — used by diff helpers. */
export type SceneNode<K extends ColumnKey = ColumnKey> =
  | CellNode<K>
  | RowOverlayNode
  | LegendNode;
